import {
    Button,
    Card,
    CardActions,
    CardContent,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import * as Yup from "yup";
import {useContext, useState} from "react";
import {useNavigate} from "react-router-dom";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {FormAsyncAutocomplete} from "../../../experiments/forms/FormAsyncAutocomplete";
import {SubmitHandler, useFieldArray, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {FormSelect} from "../../../experiments/forms/FormSelect";
import {useCashBackRepository} from '../../../api/repositories/CashbackRepository';
import FormTextField from "../../../experiments/forms/FormTextField";
import {transactionType} from '../constants/ThresholdsConstants';
import {ISelectValues} from '../constants/ISelectValues';
import * as MuiIcons from '@mui/icons-material';
import {Euro, LibraryAddRounded, Percent} from '@mui/icons-material';
import {fixThresholdValue} from '../util/CashbackUtil';
import {useCBRepository} from '../../../api/repositories/CBRepository';

interface IThresholdRow {
    threshold: string;
    percentage: string;
    typology: string;
}

type ThresholdInputs = {
    customers: string
    thresholds: IThresholdRow[],
    legalEntity: string,
    dealer: string,
}

const CreateThreshold = () => {

    const { t } = useTranslation("finance", { keyPrefix: 'cashback' });
    const { t: tg } = useTranslation("translation");
    const { dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);
    const { getCustomers } = useCBRepository();
    const { getFilteredThreshold, postThresholds } = useCashBackRepository();
    const navigate = useNavigate();

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));


    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const [focusStoreSelect, setFocusStoreSelect] = useState<boolean>(false);

    const schema = Yup.object().shape({
        customers: Yup.string().test('invalid-customers', t("filters.errors.invalid-customers"), function (value) {
            return !!value && value.length > 0;
        }),
        thresholds: Yup.array().of(
            Yup.object().shape({
                threshold: Yup.string().required(),
                percentage: Yup.number().required().transform((value, originalValue) => {
                    return originalValue === '' ? undefined : parseFloat(originalValue);
                }).max(100, t("filters.errors.invalid-percentage")).nullable().min(0, t("filters.errors.invalid-percentage")).nullable(),
                typology: Yup.string().required(),
            })
        ).min(1, 'At least one threshold is required'),
        legalEntity: Yup.string().required(),
        dealer: Yup.string().required(),
    });

    const defaultValues: ThresholdInputs = {
        customers: "",
        thresholds: [{ threshold: "", percentage: "", typology: "" }],
        legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
        dealer: dealerOptions.length == 1 ? dealerOptions[0].value : "",
    }

    const { handleSubmit, control, watch, setValue, trigger } = useForm<ThresholdInputs>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: "thresholds"
    });

    const watcher = watch();

    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setDealers([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
                return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                };
            });
        setDealers(dealers);
    }

    const onSubmit: SubmitHandler<ThresholdInputs> = (data: any) => {
        let postThreshold: any = data.thresholds;
        postThreshold.forEach((x: any) => {
            x["customer-name"] = data.customersSearch.value.name;
            x["sap-id"] = data.customersSearch.value.sapId;
            x.threshold = fixThresholdValue(x.threshold);
            x["dealer"] = data.dealer;
            x["legalEntity"] = data.legalEntity;
        });
        let unique = checkUnique(postThreshold);

        if(unique){
            getFilteredThreshold(data.customersSearch.value.name)
                .then(response => {
                    if(response && response.content){
                        let filteredArrayWithSapId = response.content.filter((it: any) => it["sap-id"] === postThreshold[0]["sap-id"]);
                        if(response.content.length === 0 || checkUniqueThresholdWithDb(postThreshold, filteredArrayWithSapId)){
                            postThresholds(postThreshold)
                                .then((data) => {
                                        dispatchGlobalData({
                                            type: setSnackbar,
                                            payload: {
                                                snackbar: {
                                                    message: t('tables.threshold.snackbar.success-create'),
                                                    type: snackbarTypes.success,
                                                    show: true,
                                                    autoClose: true
                                                }
                                            }
                                        });
                                        navigate('/finance/cashback/thresholdsconfiguration');
                                    }
                                )

                        } else {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t('tables.threshold.snackbar.same-threshold'),
                                        type: snackbarTypes.error,
                                        show: true,
                                        autoClose: true
                                    }
                                }
                            });
                        }
                    }
                })
                .catch(() => console.log("error"));
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('tables.threshold.snackbar.same-threshold'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    }

    function checkUnique(arr: any) {
        var unique = true;
        for (var i = 0; i < arr.length; i++) {
            for (var j = i + 1; j < arr.length; j++) {
                if (JSON.stringify(arr[i]) === JSON.stringify(arr[j])) {
                    unique = false;
                    break;
                }
            }
        }
        return unique;
    }


    function checkUniqueThresholdWithDb(postThreshold: any, filteredArrayWithSapId: any) {
        for (const obj of postThreshold) {
            const existsInFilteredArray = objectExistsInArray(obj, filteredArrayWithSapId);
            if (existsInFilteredArray) {
                return false;
            }
        }
        return true;
    }

    function objectExistsInArray(object: any, array: any){
        return array.some((item:any) => item.percentage === object.percentage
            &&  item.threshold === object.threshold
            &&  item.typology === object.typology )
    }

    return (
        <Card variant="outlined" sx={{ margin: '15px' }}>

            <form onSubmit={handleSubmit(onSubmit)}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item md={6} sm={12} xs={12}>
                            <FormAsyncAutocomplete
                                name={'customersSearch'}
                                control={control}
                                props={{
                                    callback: (it) => getCustomers(it),
                                    multiselect: false,
                                    getOptionLabel: (option) => option?.value?.name + " - " + option?.value?.taxCodeOrVatNumber,
                                    onSelect: (option) => {
                                        setValue('customers', option?.value?.name)
                                        trigger('customers');
                                    },
                                    renderInput: (params) =>
                                        <TextField
                                            {...params}
                                            onBlur={() => setFocusStoreSelect(false)}
                                            onFocus={() => setFocusStoreSelect(true)}
                                            label={watcher.customers?.length === 0 && !focusStoreSelect ? t('threshold-wizard.form.customers-label-all-search') : t('threshold-wizard.form.customers-label-search')}
                                        />
                                }}
                            />
                        </Grid>
                        <Grid item md={6} sm={12} xs={12}>
                            <FormTextField
                                control={control}
                                name={'customers'}
                                props={{
                                    disabled: true,
                                    label: t('threshold-wizard.form.customers-label'),
                                    sx: { width: '100%' },
                                }}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={"legalEntity"}
                                control={control}
                                props={{
                                    label: tg("shared.legalEntity"),
                                    fullWidth: true,
                                    required: true,
                                    onChange: (e: any) => {
                                        handleChangeLegalEntities(e.target.value)
                                    },
                                }}

                                items={legalEntityOptions.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={"dealer"}
                                control={control}
                                props={{
                                    label: tg("shared.dealer"),
                                    fullWidth: true,
                                    required: true,
                                    disabled: dealers.length == 0
                                }}
                                items={dealers.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
                <CardContent>

                    <Grid item xs={12}>
                        <Typography variant={'body2'} color={'primary.light'} fontWeight={700}>
                            {t('threshold-wizard.form.title')}
                        </Typography>
                        <Button
                            sx={{ float: "right" }}
                            variant={"text"} color={"accent"}
                            endIcon={<LibraryAddRounded />} onClick={() => append({ threshold: "", percentage: "", typology: "" })}>
                            {t('threshold-wizard.form.add-threshold')}
                        </Button>
                    </Grid>
                    {fields.map((f, index) =>
                        <Grid container spacing={2} alignItems={'flex-end'} key={f.id}>

                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={`thresholds.${index}.threshold`}
                                    props={{
                                        variant: "standard", fullWidth: true, label: t('threshold-wizard.form.threshold-label'),
                                        type: "number",
                                        InputProps: {
                                            startAdornment:
                                                <InputAdornment position="start">
                                                    <Euro style={{ fontSize: '15px' }} />
                                                </InputAdornment>
                                        },

                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={`thresholds.${index}.percentage`}
                                    props={{
                                        variant: "standard", fullWidth: true, label: t('threshold-wizard.form.percentage-label'),
                                        type: "number",
                                        InputProps: {
                                            startAdornment:
                                                <InputAdornment position="start">
                                                    <Percent style={{ fontSize: '15px' }} />
                                                </InputAdornment>
                                        },
                                    }}

                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormSelect
                                    name={`thresholds.${index}.typology`}
                                    control={control}
                                    props={{
                                        variant: "standard", fullWidth: true, label: t('threshold-wizard.form.typology-label')
                                    }}
                                    items={transactionType.map((item: ISelectValues, index: number) => (
                                        <MenuItem key={index} value={item.value}>
                                            {t(item.label)}
                                        </MenuItem>
                                    ))} />

                            </Grid>
                            <Grid item md={1} sm={2} xs={2}>
                                {index > 0 ?
                                    <Tooltip title={t("tables.threshold.delete")}>
                                        <IconButton color='accent' onClick={() => remove(index)}>
                                            <MuiIcons.Delete />
                                        </IconButton>
                                    </Tooltip>
                                    : null
                                }
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Button variant={"outlined"} color={"accent"} onClick={() => navigate(-1)}>{t('threshold-wizard.buttons.cancel')}</Button>
                    <Button color={"accent"} type="submit">{t('threshold-wizard.buttons.create')}</Button>
                </CardActions>
            </form>
        </Card>
    )
}

export default CreateThreshold;